/**
 * Handles listed by, name and email
 */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Grid, ThemeConstellation } from '@zillow/constellation';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import WizardAlert from '../../WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import PageWizard from '../../Wizard/PageWizard';

import { default as FormSectionContainerForRentBy } from '../ForRentBy/components/FormSectionContainer';

import EmailFormField from '../ContactInfo/components/EmailFormField';
import NameFormField from '../ContactInfo/components/NameFormField';

import { StyledDivCustomLeftSpacing } from '../../StyledComponents/StyledWizardForm';
import { WizardForm, WizardFormSectionContainer } from '../../../styles/wizard';

import WizardTipContent from './components/WizardTipContent';

const propTypes = {
  onGoToPrevStep: PropTypes.func.isRequired,
  onGoToNextStep: PropTypes.func.isRequired,
  wizardTitle: PropTypes.node.isRequired,
  progressStepper: PropTypes.node.isRequired,
  totalSteps: PropTypes.number.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  showDesktopViewBreakpoint: PropTypes.oneOf(Object.keys(ThemeConstellation.ns().breakpoints)).isRequired,
};

const defaultProps = {};

const WhoIsListingProperty = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}) => {
  const {
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="WhoIsListingProperty">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Grid
          display="grid"
          gridTemplateColumns={{
            default: 'repeat(6, 1fr)',
            // 769
            lg_gte: 'repeat(12, 1fr)',
          }}
          gap="sm"
        >
          <Grid gridColumnEnd={{ default: 'span 6', lg_gte: 'span 12', xl_gte: 'span 7', xxl_gte: 'span 7' }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e) => e.preventDefault()}>
              <div>
                <FormSectionContainerForRentBy />

                <WizardFormSectionContainer css={{ marginTop: '-16px' }}>
                  <NameFormField />

                  <EmailFormField />
                </WizardFormSectionContainer>
              </div>
            </WizardForm>
          </Grid>
          <Grid gridColumnEnd={{ default: 'span 6', lg_gte: 'span 12', xl_gte: 'span 12', xxl_gte: 'span 5' }}>
            <StyledDivCustomLeftSpacing>
              <WizardTipCardContainer
                header="Tips for contact information"
                showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              >
                <WizardTipContent />
              </WizardTipCardContainer>
            </StyledDivCustomLeftSpacing>
          </Grid>
        </Grid>
      </div>
    </PageWizard>
  );
};

WhoIsListingProperty.propTypes = propTypes;
WhoIsListingProperty.defaultProps = defaultProps;

export default WhoIsListingProperty;
